import React, { useEffect, useMemo, useRef } from "react"
import Layout from "../components/Global/Layout/Layout"
import HeroSection from "../components/Flexipage/HeroSection/HeroSection"
import AccordionRow from "../components/Flexipage/AccordionRow/AccordionRow"
import AltRow from "../components/Flexipage/AltRow/AltRow"
import CallToActionRow from "../components/Flexipage/CallToActionRow/CallToActionRow"
import CopyRow from "../components/Flexipage/CopyRow/CopyRow"
import PanelsRow from "../components/Flexipage/PanelsRow/PanelsRow"
import TestimonialsRow from "../components/Flexipage/TestimonialsRow/TestimonialsRow"
import TwoColumnsRow from "../components/Flexipage/TwoColumnsRow/TwoColumnsRow"
import WidgetRow from "../components/Flexipage/WidgetRow/WidgetRow"
import { backgroundColourFormatter, headingAltText } from "../components/Flexipage/Global/utils/flexipageHelpers"
import { FlexipageWrapper } from "../components/Flexipage/Global/FlexipageStyles"
import ResponsiveTables from "../components/Flexipage/Global/ResponsiveTables/ResponsiveTables"
import { animatePage } from "../components/Flexipage/Global/utils/animationScript"
import SEO from "../components/Global/SEO/seo"
import { graphql } from "gatsby"

const Flexipage = ({data}) => {

    function getRows(content){
        const rows = content.page_rows || [];
        let markup = [];
        let rowKeyCount = 0;
        let previousRowIndex = null;

        rows.forEach((rowData, index) => {
            const rowType = rowData.__typename;
            rowKeyCount++;
            const rowKey = `row-${rowKeyCount}`;

            if (rowType === 'Statamic_Set_PageRows_WidgetRow' && previousRowIndex !== null) {
              let widgetClass = ' widget-row-next';
              switch (rowData.widget_selector) {
                case "Payment Orchestration Platform Diagram":
                    widgetClass = ' widget-row-next-truly-global';
                    break;
                case "BNPL Logos Slider":
                    widgetClass = ' widget-row-next-bnpl-logos';
                    break;
                case "Logos Map":
                    widgetClass = ' widget-row-next-logos-map';
                    break;
                default:
                    break;
              }

              const prevBgColor = backgroundColourFormatter(markup[previousRowIndex].props.data?.background_colour?.value);
              rowData.background_colour = prevBgColor ? { value: prevBgColor } : null;

              markup[previousRowIndex] = React.cloneElement(markup[previousRowIndex], {
                  data: { ...markup[previousRowIndex].props.data, addClassToPrevious: widgetClass },
              });
            }

            let newRow;
            switch (rowType) {
                case 'Statamic_Set_PageRows_AccordionRow':
                    newRow = <AccordionRow data={rowData} key={rowKey} />;
                    break;
                case 'Statamic_Set_PageRows_AltRow':
                    newRow = <AltRow data={rowData} key={rowKey} />;
                    break;
                case 'Statamic_Set_PageRows_CallToActionRow':
                    newRow = <CallToActionRow data={rowData} key={rowKey} />;
                    break;
                case 'Statamic_Set_PageRows_CopyRow':
                    newRow = <CopyRow data={rowData} key={rowKey} />;
                    break;
                case 'Statamic_Set_PageRows_PanelsRow':
                    newRow = <PanelsRow data={rowData} key={rowKey} />;
                    break;
                case 'Statamic_Set_PageRows_TestimonialsRow':
                    newRow = <TestimonialsRow data={rowData} key={rowKey} />;
                    break;
                case 'Statamic_Set_PageRows_TwoColumnsRow':
                    newRow = <TwoColumnsRow data={rowData} key={rowKey} />;
                    break;
                case 'Statamic_Set_PageRows_WidgetRow':
                    newRow = <WidgetRow data={rowData} key={rowKey} />;
                    break;
              default:
                break
            }
            markup.push(newRow);
            previousRowIndex = index;
        });
        return markup;
    }

    const post = data.statamic.entry;
    const markup = useMemo(() => getRows(post), [post]);
    const title = headingAltText(post.title || "");
    const heroCopy = post.hero_copy;
    const heroLeadImage = post.hero_background_image;
    const heroImage = post.hero_lead_image;
    const heroIcon = post.hero_icon;
    const heroVideo = post.hero_video_url;
    const heroPanels = post.hero_panels || [];
    const metaTitle = post.meta_title || title;
    const metaDescription = post.meta_description;

    const pageRef = useRef(null);

    useEffect(() => {
      let animationTargets = pageRef.current.querySelectorAll('[data-animation-trigger]');
      animationTargets = animatePage(animationTargets);
      window.addEventListener('scroll', function(){
        if (animationTargets && animationTargets.length > 0) {
          animationTargets = animatePage(animationTargets);
        }
      })
    });

    return (
      <Layout
        headerTransparent
        whiteToggler
        logoDark={false}
        helpSectionColor={'white-bg'}
      >
        <SEO title={metaTitle} description={metaDescription} />
        <FlexipageWrapper ref={pageRef}>
          <HeroSection
            title={title}
            heroCopy={heroCopy}
            heroLeadImage={heroLeadImage}
            heroVideo={heroVideo}
            heroImage={heroImage}
            heroIcon={heroIcon}
            heroPanels={heroPanels}
          />
          <ResponsiveTables className="flexipage-rows">
            {markup}
          </ResponsiveTables>
        </FlexipageWrapper>
      </Layout>
    )
};

export default Flexipage

export const query = graphql`
query GetFlexiPage($slug: String!) {
  statamic {
    entry(collection: "flexipage", slug: $slug) {
      id
      slug
      title
      ... on Statamic_Entry_Flexipage_Flexipage {
        meta_title
        meta_description
        hero_copy
        hero_video_url
        hero_background_image {
          url
          permalink
          ... on Statamic_Asset_Assets {
            alt
            width
            height
          }
        }
        hero_lead_image {
          url
          permalink
          ... on Statamic_Asset_Assets {
            alt
            width
            height
          }
        }
        hero_icon {
          url
          permalink
          ... on Statamic_Asset_Assets {
            alt
            width
            height
          }
        }
        hero_panels {
          label
          link
          icon {
            url
            permalink
            ... on Statamic_Asset_Assets {
              alt
              width
              height
            }
          }
        }
        page_rows {
          __typename
          ... on Statamic_Set_PageRows_CopyRow {
            row_id
            text_alignment { value }
            background_colour { value }
            copy {
              __typename
              ... on Statamic_BardText {
                type
                text
              }
              ... on Statamic_Set_PageRows_Copy_Video {
                video_url
              }
              ... on Statamic_Set_PageRows_Copy_CallToAction {
                label
                link
                icon {
                  url
                  permalink
                  ... on Statamic_Asset_Assets {
                    alt
                    width
                    height
                  }
                }
              }
              ... on Statamic_Set_PageRows_Copy_CodeEmbed {
                embed_html
                embed_script_url
                embed_script
              }
            }
          }
          ... on Statamic_Set_PageRows_AltRow {
            row_id
            row_alignment { value }
            background_colour { value }
            alt_row_items {
              ... on Statamic_Set_PageRows_AltRowItems_AltRowItem {
                video_url
                copy {
                  __typename
                  ... on Statamic_BardText {
                    type
                    text
                  }
                  ... on Statamic_Set_PageRows_AltRowItems_Copy_Video {
                    video_url
                  }
                }
                image {
                  url
                  permalink
                  ... on Statamic_Asset_Assets {
                    alt
                    width
                    height
                  }
                }
              }
            }
          }
          ... on Statamic_Set_PageRows_PanelsRow {
            row_id
            row_style
            column_count
            copy {
              __typename
              ... on Statamic_BardText {
                type
                text
              }
              ... on Statamic_Set_PageRows_Copy_Video {
                video_url
              }
              ... on Statamic_Set_PageRows_Copy_CallToAction {
                label
                link
                icon {
                  url
                  permalink
                  ... on Statamic_Asset_Assets {
                    alt
                    width
                    height
                  }
                }
              }
              ... on Statamic_Set_PageRows_Copy_CodeEmbed {
                embed_html
                embed_script_url
                embed_script
              }
            }
            panels {
              title
              copy
            }
          }
          ... on Statamic_Set_PageRows_AccordionRow {
            row_id
            background_colour { value }
            copy {
              __typename
              ... on Statamic_BardText {
                type
                text
              }
              ... on Statamic_Set_PageRows_Copy_Video {
                video_url
              }
              ... on Statamic_Set_PageRows_Copy_CallToAction {
                label
                link
                icon {
                  url
                  permalink
                  ... on Statamic_Asset_Assets {
                    alt
                    width
                    height
                  }
                }
              }
              ... on Statamic_Set_PageRows_Copy_CodeEmbed {
                embed_html
                embed_script_url
                embed_script
              }
            }
            accordion_items {
              title
              copy
            }
          }
          ... on Statamic_Set_PageRows_WidgetRow {
            row_id
            widget_selector
          }
          ... on Statamic_Set_PageRows_CallToActionRow {
            row_id
            label
            tag_line
            link_text
            link_url
          }
          ... on Statamic_Set_PageRows_TestimonialsRow {
            row_id
            background_colour { value }
            testimonials {
              testimonial_name
              position
              copy
              image {
                url
                permalink
                ... on Statamic_Asset_Assets {
                  alt
                  width
                  height
                }
              }
            }
          }
          ... on Statamic_Set_PageRows_TwoColumnsRow {
            row_id
            row_subtitle
            background_colour { value }
            text_alignment { value }
            column_1_copy
            column_2_copy
          }
        }
      }
    }
  }
}
`
